/**
 * OperationsModule.js
 *
 * Main Operations module - Three-tab architecture: Voyage | Checklists | Logbook
 * Settings accessed via gear icon in Checklists tab (embedded pattern)
 *
 * ANCHOR MODE compliant - follows Domain Spine v2
 *
 * PHASE 3 UPDATE: Mobile full-screen voyage view
 * - When an active voyage is open on mobile, tabs are hidden
 * - Back button returns to tabs view
 */

import React, { useState, useCallback, useMemo } from 'react';
// FT-356c: unused ArrowLeft import removed.

// Shared components
import TabNavigation from '../../TabNavigation';

// FT-357: shell-level back handler — register a "back to dashboard" handler
// so mobile users always see a Back button in the bottom bar.
import { useSetBackHandler } from '../../../../contexts/NavigationBackContext';

// Domain hooks
import { useVoyages } from '../../../../hooks/domain/operations/useVoyages';
import { useOperationalChecklists } from '../../../../hooks/domain/operations/useOperationalChecklists';
import { useMainLogbook } from '../../../../hooks/domain/operations/useMainLogbook';
import { useChecklistRecords } from '../../../../hooks/domain/operations/useChecklistRecords';
import { ChecklistRecordsProvider } from '../../../../hooks/domain/operations/ChecklistRecordsContext';

// Core hooks
import { useVessel } from '../../../../contexts/VesselContext';
import { usePermissions } from '../../../../hooks/usePermissions';
import { useCustomDialogs } from '../../../../hooks/useCustomDialogs';

// Tab components
import VoyageTab from './VoyageTab';
import ChecklistsTab from './ChecklistsTab';
import LogbookTab from './LogbookTab';

/**
 * Tab configuration - Settings removed (accessed via gear icon in Checklists)
 */
const TABS = [
  { id: 'voyage', label: 'Voyage' },
  { id: 'checklists', label: 'Checklists' },
  { id: 'logbook', label: 'Logbook' }
];

/**
 * OperationsModule - Main shell component
 *
 * PHASE 3 UPDATE: voyageFullScreen state lifted to TailwindShell
 * so app header and bottom nav can be hidden on mobile
 */
const OperationsModule = ({ setActiveModule, voyageFullScreen, setVoyageFullScreen }) => {
  // Tab state
  const [activeTab, setActiveTab] = useState('voyage');

  // FT-357: register a shell-level "back to dashboard" handler so mobile
  // users always see a Back button in the bottom bar for this module.
  // Any drill-down inside the module that calls useSetBackHandler pushes on
  // top of this and pops back to it on unmount.
  useSetBackHandler(
    useCallback(() => {
      if (setActiveModule) setActiveModule('dashboard');
    }, [setActiveModule])
  );

  // Context and hooks
  const { getCurrentVessel } = useVessel();
  const currentVessel = getCurrentVessel();
  const { can, currentUser } = usePermissions();
  const { showAlert, showConfirm, AlertDialog, ConfirmDialog } = useCustomDialogs();

  // Domain hooks
  // FT-324 cycle 3 (2026-07-07): `useChecklistRecords` hoisted here alongside
  // `useMainLogbook` — same eager-mount pattern that reliably survives
  // cross-device UI refresh. All 6 consumers now read this single instance via
  // `ChecklistRecordsProvider` below. See ChecklistRecordsContext.js for the
  // full rationale (mount-race + missed hydrate listener when hook mounted
  // lazily inside ChecklistsTab).
  const voyageHook = useVoyages();
  const checklistHook = useOperationalChecklists();
  const logbookHook = useMainLogbook();
  const checklistRecordsHook = useChecklistRecords();

  // Destructure commonly used values
  const { activeVoyage } = voyageHook;
  const { getActivePermits } = checklistHook;

  // Get active permits for badge
  const activePermits = useMemo(() => {
    return getActivePermits();
  }, [getActivePermits]);

  // Check for critical permits
  const criticalPermits = useMemo(() => {
    return activePermits.filter(p => p.warningLevel === 'critical');
  }, [activePermits]);

  // Handle tab change
  const handleTabChange = useCallback((tabId) => {
    setActiveTab(tabId);
  }, []);

  // Context to pass to tabs
  const tabContext = useMemo(() => ({
    vessel: currentVessel,
    can,
    currentUser,
    showAlert,
    showConfirm,
    voyage: voyageHook,
    checklists: checklistHook,
    logbook: logbookHook,
    // FT-324 cycle 3: included in tabContext for parity with logbook/voyage,
    // but the primary consumption route is `useChecklistRecordsContext()` via
    // the ChecklistRecordsProvider below — no prop-drilling required.
    checklistRecords: checklistRecordsHook,
    onSwitchTab: handleTabChange,
    setActiveModule,  // For navigating back to other modules (e.g., dashboard)
    // Phase 3: Mobile full-screen voyage support
    voyageFullScreen,
    setVoyageFullScreen
  }), [currentVessel, can, currentUser, showAlert, showConfirm, voyageHook, checklistHook, logbookHook, checklistRecordsHook, handleTabChange, setActiveModule, voyageFullScreen, setVoyageFullScreen]);

  // Render active tab content
  const renderTabContent = () => {
    switch (activeTab) {
      case 'voyage':
        return <VoyageTab context={tabContext} />;
      case 'checklists':
        return <ChecklistsTab context={tabContext} />;
      case 'logbook':
        return <LogbookTab context={tabContext} />;
      default:
        return <VoyageTab context={tabContext} />;
    }
  };

  // Build tabs with badges
  const tabsWithBadges = useMemo(() => {
    return TABS.map(tab => {
      let badge = null;

      // Add "Active" badge to Voyage tab when voyage is underway
      if (tab.id === 'voyage' && activeVoyage) {
        badge = (
          <span className="ml-2 px-1.5 py-0.5 text-xs bg-green-100 text-green-700 rounded-full">
            Active
          </span>
        );
      }

      // Add permit count badge to Checklists tab
      if (tab.id === 'checklists' && activePermits.length > 0) {
        const hasCritical = criticalPermits.length > 0;
        badge = (
          <span className={`ml-2 px-1.5 py-0.5 text-xs rounded-full ${
            hasCritical
              ? 'bg-red-100 text-red-700'
              : 'bg-amber-100 text-amber-700'
          }`}>
            {activePermits.length}
          </span>
        );
      }

      return { ...tab, badge };
    });
  }, [activeVoyage, activePermits, criticalPermits]);

  // Mobile full-screen voyage view (Phase 3)
  // When voyageFullScreen is true AND there's an active voyage, hide header/tabs on mobile
  const showMobileFullScreen = voyageFullScreen && activeVoyage;

  return (
    // FT-324 cycle 3: single ChecklistRecordsProvider wraps the entire
    // Operations shell so every descendant (tabs, execution views, closure
    // modal) reads the SAME hook instance mounted eagerly at line ~66. This
    // is the load-bearing mount-timing fix — the hook, its useStorageState
    // and its sr:sync:hydrate listener all live for the whole Operations
    // session instead of only while the Checklists tab is active.
    <ChecklistRecordsProvider value={checklistRecordsHook}>
      <div>
        {/* Module Header - hidden on mobile when in full-screen voyage mode */}
        <div className={`mb-8 ${showMobileFullScreen ? 'hidden md:block' : ''}`}>
          <h1 className="text-2xl font-bold text-gray-900">Operations</h1>
          <p className="mt-1 text-sm text-gray-500">
            Manage voyages, operational checklists, and vessel logbook
          </p>
        </div>

        {/* Standard TabNavigation - hidden on mobile when in full-screen voyage mode */}
        <div className={showMobileFullScreen ? 'hidden md:block' : ''}>
          <TabNavigation
            tabs={tabsWithBadges}
            activeTab={activeTab}
            onTabChange={handleTabChange}
          />
        </div>

        {/* Tab Content */}
        <div className={showMobileFullScreen ? '' : 'mt-6'}>
          {renderTabContent()}
        </div>

        {/* Dialogs */}
        <AlertDialog />
        <ConfirmDialog />
      </div>
    </ChecklistRecordsProvider>
  );
};

export default OperationsModule;
